import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  Wallet,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowUpRight,
  ShieldAlert,
  Calendar,
  Sparkles,
  Receipt,
} from 'lucide-react';
import {
  calculateMemberFinancials,
  toDhakaDateTime,
  isLateSubmission,
  getDhakaMonthDeadlineUtcMs,
} from '../../../../shared/utilities/calculations';
import type { Contribution } from '../../../../shared/types';

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user, member, emailVerified } = useAuth();
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to user's contributions in Firestore
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'contributions'),
      where('memberId', '==', user.uid),
      orderBy('month', 'desc')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: Contribution[] = [];
        snap.forEach((doc) => list.push(doc.data() as Contribution));
        setContributions(list);
        setLoading(false);
      },
      (err) => {
        console.warn('[FHPF] Offline contributions load:', err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user]);

  const nowDhaka = toDhakaDateTime();
  const currentMonthStr = nowDhaka.isoMonth; // e.g. "2026-05"

  // Calculate financial summary using shared calculation engine
  const financialSummary = member
    ? calculateMemberFinancials({
        member,
        groupStartDate: '2024-01-01',
        currentDate: new Date(),
        approvedContributions: contributions,
      })
    : null;

  // Current month's contribution record if any
  const currentMonthContribution = contributions.find((c) => c.month === currentMonthStr);

  // Status of current month
  const currentMonthStatus = currentMonthContribution
    ? currentMonthContribution.status
    : isLateSubmission(currentMonthStr, new Date())
    ? 'unpaid'
    : 'unpaid';

  // Format currency
  const fmt = (n: number) => `৳${n.toLocaleString('en-US')}`;

  return (
    <div className="space-y-6 pb-20 sm:pb-8">
      {/* Email Verification Banner */}
      {!emailVerified && (
        <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-amber-800 dark:text-amber-200">
          <div className="flex items-start gap-2.5">
            <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
            <p className="text-xs sm:text-sm font-medium">{t('auth.emailUnverifiedWarning')}</p>
          </div>
          <Link
            to="/profile"
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white transition-colors shrink-0"
          >
            {t('nav.profile')}
          </Link>
        </div>
      )}

      {/* Header Welcome Card */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-700 via-primary-800 to-navy-950 text-white p-6 sm:p-8 shadow-xl">
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-primary-200 text-xs sm:text-sm font-medium mb-1">
            <Calendar className="w-4 h-4" />
            <span>{nowDhaka.formattedString} (Dhaka Time, UTC+6)</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            {t('dashboard.welcome')}, {member?.name || user?.displayName || 'Member'}
          </h1>
          <p className="text-xs sm:text-sm text-primary-100/90 mt-1 max-w-xl">
            {t('dashboard.deadlineCountdown')}
          </p>
        </div>

        {/* Decorative circle glow */}
        <div className="absolute -right-10 -bottom-10 w-64 h-64 rounded-full bg-primary-500/20 blur-3xl pointer-events-none" />
      </div>

      {/* Financial Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {/* Approved Total */}
        <div className="p-4 sm:p-5 rounded-2xl bg-white dark:bg-navy-900 border border-gray-200/80 dark:border-navy-800 shadow-sm">
          <div className="flex items-center justify-between mb-2 text-emerald-600 dark:text-emerald-400">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('dashboard.approvedTotal')}
            </span>
            <Wallet className="w-4 h-4" />
          </div>
          <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {fmt(financialSummary?.approvedTotal ?? (member?.approvedTotal || 0))}
          </div>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
            {financialSummary?.approvedMonthsCount || 0} months verified
          </p>
        </div>

        {/* Due Amount */}
        <div className="p-4 sm:p-5 rounded-2xl bg-white dark:bg-navy-900 border border-gray-200/80 dark:border-navy-800 shadow-sm">
          <div className="flex items-center justify-between mb-2 text-red-500 dark:text-red-400">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('dashboard.dueAmount')}
            </span>
            <AlertCircle className="w-4 h-4" />
          </div>
          <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {fmt(financialSummary?.dueAmount || 0)}
          </div>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
            {(financialSummary?.unpaidMonths?.length || 0)} unpaid months
          </p>
        </div>

        {/* Advance Balance */}
        <div className="p-4 sm:p-5 rounded-2xl bg-white dark:bg-navy-900 border border-gray-200/80 dark:border-navy-800 shadow-sm">
          <div className="flex items-center justify-between mb-2 text-primary-500">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('dashboard.advanceAmount')}
            </span>
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {fmt(financialSummary?.advanceAmount || 0)}
          </div>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
            credited to future dues
          </p>
        </div>

        {/* Total Late Fees */}
        <div className="p-4 sm:p-5 rounded-2xl bg-white dark:bg-navy-900 border border-gray-200/80 dark:border-navy-800 shadow-sm">
          <div className="flex items-center justify-between mb-2 text-amber-500">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('dashboard.totalLateFees')}
            </span>
            <Clock className="w-4 h-4" />
          </div>
          <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {fmt(financialSummary?.expectedLateFees || 0)}
          </div>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
            ৳200 per late month
          </p>
        </div>
      </div>

      {/* Current Month Status & Fast Submission Callout */}
      <div className="p-5 sm:p-6 rounded-2xl bg-white dark:bg-navy-900 border border-gray-200/80 dark:border-navy-800 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('dashboard.status')}: {currentMonthStr}
            </span>
            {currentMonthStatus === 'approved' && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                {t('status.approved')}
              </span>
            )}
            {currentMonthStatus === 'pending' && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                {t('status.pending')}
              </span>
            )}
            {currentMonthStatus === 'rejected' && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300">
                {t('status.rejected')}
              </span>
            )}
            {currentMonthStatus === 'unpaid' && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-navy-800 dark:text-gray-300">
                {t('status.unpaid')}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {currentMonthStatus === 'approved'
              ? t('dashboard.dueClear')
              : 'Submit your bank deposit screenshot before the 10th to avoid the ৳200 late fee.'}
          </p>
        </div>

        {currentMonthStatus !== 'approved' && (
          <Link
            to="/submit"
            className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-md shadow-primary-600/20 transition-all shrink-0"
          >
            <span>{t('dashboard.submitContribution')}</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        )}
      </div>

      {/* Recent Contributions List */}
      <div className="rounded-2xl bg-white dark:bg-navy-900 border border-gray-200/80 dark:border-navy-800 shadow-sm p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-gray-900 dark:text-white text-base flex items-center gap-2">
            <Receipt className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            <span>{t('dashboard.recentContributions')}</span>
          </h2>
          <Link
            to="/history"
            className="text-xs text-primary-600 dark:text-primary-400 hover:underline font-semibold"
          >
            {t('dashboard.viewHistory')}
          </Link>
        </div>

        {loading ? (
          <div className="py-8 text-center text-sm text-gray-400">Loading contributions...</div>
        ) : contributions.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-400">
            {t('dashboard.noContributions')}
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-navy-800">
            {contributions.slice(0, 5).map((c) => (
              <div key={c.contributionId} className="py-3.5 flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold text-sm text-gray-900 dark:text-white">
                    {c.month}
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    {c.submittedInDhakaTime || c.submittedAt}
                  </div>
                </div>

                <div className="text-right">
                  <div className="font-bold text-sm text-gray-900 dark:text-white">
                    {fmt(c.totalAmount || c.amount + (c.lateFee || 0))}
                  </div>
                  <span
                    className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                      c.status === 'approved'
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                        : c.status === 'rejected'
                        ? 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                    }`}
                  >
                    {t(`status.${c.status}`)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
